import type { InFieldCDMLocationConfig } from '../../../features/app-config/cdm-types';
import { hashCodeForInstanceId } from '../../../utils/hashcode';
import { type InfieldIdmConfig } from '../types';

export type DataFilters = InFieldCDMLocationConfig['dataFilters'];

export const getUniqueInstanceSpaces = (
  dataFilters: DataFilters
): string[] | undefined => {
  if (!dataFilters) {
    return undefined;
  }

  const instanceSpaces = Object.values(dataFilters)
    .flatMap((filter) => filter?.instanceSpaces ?? [])
    .filter(
      (space): space is string => typeof space === 'string' && space !== ''
    );

  return instanceSpaces.length > 0 ? [...new Set(instanceSpaces)] : undefined;
};

/**
 * Converts a CDM InFieldCDMLocationConfig to the legacy InfieldIdmConfig format.
 *
 * Used to maintain backwards compatibility with AppConfigContext which expects
 * the older location structure.
 *
 * @param infieldLocationConfig - The CDM location config to convert
 * @returns The converted legacy InfieldIdmConfig
 */
export const convertInfieldLocationConfigToLocation = (
  infieldLocationConfig: InFieldCDMLocationConfig
): InfieldIdmConfig => {
  return {
    externalId: infieldLocationConfig.externalId,
    id: hashCodeForInstanceId(
      infieldLocationConfig.externalId,
      infieldLocationConfig.space
    ),
    name: infieldLocationConfig.name ?? '',
    createdTime: infieldLocationConfig.createdTime ?? 0,
    lastUpdatedTime: infieldLocationConfig.lastUpdatedTime ?? 0,
    appInstanceSpace: infieldLocationConfig.dataStorage?.appInstanceSpace,
    instanceSpaces: getUniqueInstanceSpaces(infieldLocationConfig.dataFilters),
    featureToggles: infieldLocationConfig.featureToggles,
    space: infieldLocationConfig.space ?? '',
  };
};
